import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import _, { isString, noop } from 'lodash';
import moment from 'moment';

import { SaveStatus } from '../../shared/SavePanel';
import { ToggleSwitch } from '../../shared/ToggleSwitch/ToggleSwitch';
import {
    BannerAction,
    PossibleBannerActionParameters,
} from '../BannerAction/BannerAction';
import { BannerLocationSelector } from '../BannerLocationSelector/BannerLocationSelector';
import { BannerMedia } from '../BannerMedia/BannerMedia';
import { BannerTranslations } from '../BannerTranslations/BannerTranslations';
import { LabelInput, NewRow, SavePanel, Section } from '../../shared';
import { LanguageType } from '@lifeserver/shared';

import { useProcessedMediaStatuses } from '../../hooks/medias';

import { toHTMLDate, toJSONDate } from '../../utils/dates';
import { ImageFileUploading } from '../../utils/upload';
import { computeSectionAttention } from '../../utils/utils';
import { mediaTypeToeExpectedDimensions } from '../../utils/validators';
import {
    getDefaultBannerTypeAndPlacementByScene,
    getDefaultParametersByActionType,
    hasSubtitle,
} from '../utils';

import {
    BannerBackgroundTypeByBannerType,
    BannerPositionScene,
} from '../../types/enums/bannerType.enum';
import { MediaType } from '../../types/enums/mediatype.enum';

import {
    Banner,
    BannerActionDistrict,
    BannerActionPack,
    BannerActionScene,
    BannerActionTab,
    BannerTranslationsType,
    CreateOrUpdateBanner,
} from '../../types/bannerTypes';
import { Media } from '../../types/mediaTypes';

import { createEmptyBanner, EmptyBanner } from '../emptyBanner';
import validate from '../validate';
import styles from './BannerDetails.module.scss';

export enum PossibleBannerFields {
    Name = 'name',
    Type = 'type',
    Title = 'title',
    Subtitle = 'subtitle',
    ActiveFromDate = 'activeFromDate',
    ActiveToDate = 'activeToDate',
    Placement = 'positionPlacement',
    StringId = 'stringId',
}

type BannerDetailsProps = {
    language: LanguageType;
    isNew: boolean;
    initialBanner: Banner | EmptyBanner;
    onSave: (banner: CreateOrUpdateBanner) => Promise<boolean>;
    onDelete?: (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
    isSaving: boolean;
};

type MediaOrImageFileUploading = Media | (ImageFileUploading & { id?: number });

export const BannerDetails = (props: BannerDetailsProps) => {
    const {
        language,
        isNew,
        initialBanner,
        isSaving,
        onSave,
        onDelete = () => Promise.resolve(),
    } = props;
    const [banner, setBanner] = useState<Banner | EmptyBanner>(initialBanner);
    const [backgroundMedia, setBackgroundMedia] = useState<
        MediaOrImageFileUploading | undefined
    >('backgroundMedia' in banner ? banner.backgroundMedia : undefined);
    const [isSavedStatusShown, setIsSavedStatusShown] =
        useState<boolean>(false);
    const {
        getStatus: getProcessedStatus,
        onProcessingComplete,
        onProcessingError,
    } = useProcessedMediaStatuses();

    const hasUnsavedChanges = useMemo(() => {
        const existingBackgroundMedia =
            'backgroundMedia' in banner ? banner.backgroundMedia : undefined;

        return (
            !_.isEqual(banner, initialBanner) ||
            !_.isEqual(existingBackgroundMedia, backgroundMedia)
        );
    }, [banner, initialBanner, backgroundMedia]);

    const setHasUnsavedChanges = (hasUnsavedChanges: boolean) => {
        localStorage.setItem('hasUnsavedChanges', String(hasUnsavedChanges));
    };

    useEffect(() => {
        setHasUnsavedChanges(hasUnsavedChanges);
    }, [hasUnsavedChanges]);

    useEffect(() => {
        return () => {
            // Executed on the component unmount
            setHasUnsavedChanges(false);
        };
    }, []);

    useEffect(() => {
        if (isSavedStatusShown) {
            setTimeout(() => {
                setIsSavedStatusShown(false);
            }, 2000);
        }
    }, [isSavedStatusShown]);

    const onSceneChange = (e: ChangeEvent<HTMLInputElement>) => {
        const scene = e.target.value as unknown as BannerPositionScene;
        const { type, placement } =
            getDefaultBannerTypeAndPlacementByScene(scene);

        setBanner({
            ...createEmptyBanner(),
            type,
            positionPlacement: placement,
            positionScene: scene,
        });
        setBackgroundMedia(undefined);
    };

    const onActionTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
        const actionType = e.target.value as unknown as Banner['actionType'];
        const actionParameters = getDefaultParametersByActionType(actionType);

        setBanner((banner) => ({
            ...banner,
            actionType,
            actionParameters,
        }));
    };

    const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name as PossibleBannerFields;
        const value: string = e.target.value;
        const data = _.cloneDeep(banner);

        if (
            name === PossibleBannerFields.Title ||
            name === PossibleBannerFields.Subtitle
        ) {
            data[name] = data[name] || {};
            (data[name] as Record<LanguageType, string>)[language] = value;
        } else if (
            name === PossibleBannerFields.ActiveFromDate ||
            name === PossibleBannerFields.ActiveToDate
        ) {
            (data[name] as string) = toJSONDate(value);
        } else {
            (data[name as PossibleBannerFields] as string) = value.trim();
        }

        setBanner(data);
    };

    const onActionInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name as PossibleBannerActionParameters;
        const value: string | number = isString(e.target.value)
            ? e.target.value.trim()
            : e.target.value;
        const data = _.cloneDeep(banner);

        if (!data.actionParameters) {
            data.actionParameters = getDefaultParametersByActionType(
                data.actionType,
            );
        }

        if (name === PossibleBannerActionParameters.PackId) {
            (data.actionParameters as BannerActionPack).packId = value;
        } else if (name === PossibleBannerActionParameters.SceneName) {
            (data.actionParameters as BannerActionScene).sceneName = value;
        } else if (name === PossibleBannerActionParameters.XPosition) {
            (data.actionParameters as BannerActionScene).xPosition =
                Number(value);
        } else if (name === PossibleBannerActionParameters.TabStringId) {
            (data.actionParameters as BannerActionTab).tabStringId = value;
        } else if (name === PossibleBannerActionParameters.DistrictId) {
            (data.actionParameters as BannerActionDistrict).districtId = value;
        }

        setBanner(data);
    };

    const onIsDefaultToggle = () => {
        setBanner((banner) => {
            const isDefault = !banner.isDefault;

            if (isDefault) {
                return {
                    ...banner,
                    activeFromDate: toJSONDate(
                        moment().startOf('day').valueOf(),
                    ),
                    activeToDate: toJSONDate(new Date(2050, 11, 31)),
                    isDefault,
                };
            }

            return {
                ...banner,
                activeFromDate: toJSONDate(initialBanner?.activeFromDate),
                activeToDate: toJSONDate(initialBanner?.activeToDate),
                isDefault,
            };
        });
    };

    const onPhotosSelected = (
        _type: MediaType,
        files: ImageFileUploading[],
    ) => {
        if (!files.length) {
            return;
        }

        setBackgroundMedia(files[0]);
    };

    const onPhotosWasUploaded = (
        _type: MediaType,
        _file: File,
        res: AxiosResponse<{ uploaded: Media }>,
    ) => {
        setBackgroundMedia(
            _.assign(res.data.uploaded as Media, {
                uploading: false,
            }),
        );
    };

    const onTranslationsLoaded = (translations: BannerTranslationsType) => {
        setBanner((banner) => ({
            ...banner,
            title: translations.title,
            subtitle: hasSubtitle(banner) ? translations.subtitle : null,
        }));
    };

    const getSaveStatus = (): SaveStatus => {
        if (isSaving) {
            return SaveStatus.SAVING;
        }

        if (hasUnsavedChanges) {
            return SaveStatus.HAS_CHANGED;
        }

        return SaveStatus.NO_CHANGES;
    };

    const getBannerCreateUpdateBody = (): CreateOrUpdateBanner => {
        const body: CreateOrUpdateBanner = {
            ..._.omit(banner as Banner, ['backgroundMedia', 'id']),
            backgroundMediaId:
                backgroundMedia?.id ||
                (banner as Banner).backgroundMedia?.id ||
                NaN,
        };

        return body;
    };

    const save = async () => {
        const isSaved = await onSave(getBannerCreateUpdateBody());

        if (isSaved) {
            setHasUnsavedChanges(false);
            setIsSavedStatusShown(true);
        }
    };

    const mediaItems = [
        backgroundMedia,
        (initialBanner as Banner).backgroundMedia,
    ]
        .filter((m) => !!m)
        .slice(0, 1) as MediaOrImageFileUploading[];

    const bannerBackgroundType = BannerBackgroundTypeByBannerType[banner.type];
    const dimensions =
        mediaTypeToeExpectedDimensions[bannerBackgroundType] || {};
    const { w = 1024, h = 1024 } = Array.isArray(dimensions)
        ? dimensions[0]
        : dimensions;
    const validationResults = validate(getBannerCreateUpdateBody(), {
        backgroundMediaStatus: getProcessedStatus(mediaItems[0]),
    });
    const sectionAttention = (fieldPaths: string[]) =>
        computeSectionAttention(validationResults, fieldPaths);

    return (
        <>
            <Section
                title={isNew ? `New Banner: ${banner.name}` : banner.name}
                attention={sectionAttention([
                    'name',
                    'stringId',
                    'title',
                    'subtitle',
                    'positionScene',
                    'positionPlacement',
                    'type',
                ])}
            >
                <BannerLocationSelector
                    isNew={isNew}
                    scene={banner.positionScene}
                    placement={banner.positionPlacement}
                    type={banner.type}
                    onPlacementChange={onInputChange}
                    onSceneChange={onSceneChange}
                    onTypeChange={onInputChange}
                />
                <NewRow />
                <LabelInput
                    size="semiwide"
                    name={PossibleBannerFields.Name}
                    value={banner.name || ''}
                    tooltip="Internal use"
                    label="Banner name"
                    onChange={onInputChange}
                />
                <NewRow />
                <BannerTranslations
                    language={language}
                    banner={banner}
                    onInputChange={onInputChange}
                    onTranslationsLoaded={onTranslationsLoaded}
                />
            </Section>
            <Section
                title="Banner behaviour"
                attention={sectionAttention([
                    'activeFromDate',
                    'activeToDate',
                    'activeToDateCopy',
                    'isDefault',
                ])}
            >
                <div className={styles.defaultToggle}>
                    <ToggleSwitch
                        checked={banner.isDefault}
                        onToggle={onIsDefaultToggle}
                    />
                    Set as default
                </div>

                <LabelInput
                    size="normal"
                    type="datetime-local"
                    name={PossibleBannerFields.ActiveFromDate}
                    value={toHTMLDate(banner.activeFromDate)}
                    disabled={banner.isDefault}
                    label="Starts"
                    onChange={onInputChange}
                />
                <LabelInput
                    size="normal"
                    type="datetime-local"
                    name={PossibleBannerFields.ActiveToDate}
                    value={toHTMLDate(banner.activeToDate)}
                    disabled={banner.isDefault}
                    label="Ends"
                    onChange={onInputChange}
                />
            </Section>
            <Section
                title="Banner media"
                attention={sectionAttention([
                    'backgroundMediaId',
                    'backgroundMediaStatus',
                ])}
            >
                <BannerMedia
                    type={bannerBackgroundType}
                    header={`Banner background image (${w} x ${h}, PNG)`}
                    filename={banner.name}
                    items={mediaItems}
                    onMediaSelect={onPhotosSelected}
                    onMediaWasUploaded={onPhotosWasUploaded}
                    onMediaDragEnd={noop}
                    buttonLabel={mediaItems.length ? 'Replace' : 'Add new'}
                    onProcessingComplete={onProcessingComplete}
                    onProcessingError={onProcessingError}
                />
            </Section>
            <Section
                title="Banner link"
                attention={sectionAttention(['actionType', 'actionParameters'])}
            >
                <BannerAction
                    type={banner.actionType}
                    parameters={banner.actionParameters}
                    onInputChange={onActionInputChange}
                    onTypeChange={onActionTypeChange}
                />
            </Section>
            <SavePanel
                title={`Working with: ${banner.name || 'no name'}`}
                validationResults={validationResults}
                item={banner}
                save={save}
                showDelete={!isNew}
                saveStatus={
                    isSavedStatusShown ? SaveStatus.SAVED : getSaveStatus()
                }
                onDelete={onDelete}
            />
        </>
    );
};
